import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { SystemVersion } from '../api/entities.js';
import { System } from '../api/entities.js';
import { User } from '../api/entities.js';
import { Button } from "../components/ui/button.jsx";
import { Input } from "../components/ui/input.jsx";
import { Textarea } from "../components/ui/textarea.jsx";
import { Label } from "../components/ui/label.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Skeleton } from "../components/ui/skeleton.jsx";
import { Switch } from "../components/ui/switch.jsx";
import { ArrowLeft, Save, Calendar } from 'lucide-react';
import DatePicker from '../components/ui/date-picker.jsx';

export default function NewSystemVersion() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [systems, setSystems] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [version, setVersion] = useState({
    system_id: '',
    version_number: '',
    release_date: new Date().toISOString().split('T')[0],
    description: '',
    changelog: '',
    is_stable: true,
    is_latest: false,
    download_url: '',
    requirements: ''
  });

  // Opções de estabilidade
  const stabilityOptions = [
    { value: false, label: 'Em desenvolvimento' },
    { value: true, label: 'Estável' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Verificar se é para um sistema específico
      const urlParams = new URLSearchParams(window.location.search);
      const systemId = urlParams.get('system_id');
      
      // Carregar sistemas
      const systemsData = await System.list();
      setSystems(systemsData);
      
      if (systemId) {
        setVersion(prev => ({
          ...prev,
          system_id: systemId
        }));
      }
    } catch (error) {
      console.error('Error loading systems:', error);
    }
    setIsLoading(false);
  };

  const handleInputChange = (field, value) => {
    setVersion(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      const newVersion = await SystemVersion.create(version);
      navigate(createPageUrl(`ViewSystemVersion?id=${newVersion.id}`));
    } catch (error) {
      console.error('Error creating system version:', error);
      alert('Erro ao criar versão. Por favor, tente novamente.');
    }
    
    setIsSaving(false);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-[600px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(createPageUrl('Systems'))}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Nova Versão</h1>
            <p className="text-muted-foreground mt-1">Adicionar versão ao sistema</p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Informações da Versão</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="system">Sistema</Label>
                <Select 
                  value={version.system_id} 
                  onValueChange={(value) => handleInputChange('system_id', value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o sistema" />
                  </SelectTrigger>
                  <SelectContent>
                    {systems.map((system) => (
                      <SelectItem key={system.id} value={system.id}>
                        {system.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="version_number">Número da Versão</Label>
                <Input
                  id="version_number"
                  placeholder="Ex: 1.0.0"
                  value={version.version_number}
                  onChange={(e) => handleInputChange('version_number', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="release_date">Data de Lançamento</Label>
                <DatePicker
                  selected={version.release_date}
                  onSelect={(date) => handleInputChange('release_date', date.toISOString().split('T')[0])}
                  placeholder="Selecione a data"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="is_stable">Estabilidade</Label>
                <Select 
                  value={version.is_stable.toString()} 
                  onValueChange={(value) => handleInputChange('is_stable', value === 'true')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a estabilidade" />
                  </SelectTrigger>
                  <SelectContent>
                    {stabilityOptions.map((option) => (
                      <SelectItem key={option.value.toString()} value={option.value.toString()}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="download_url">URL de Download (opcional)</Label>
                <Input
                  id="download_url"
                  placeholder="https://exemplo.com/download/v1.0.0.zip"
                  value={version.download_url}
                  onChange={(e) => handleInputChange('download_url', e.target.value)}
                />
              </div>

              <div className="flex items-center space-x-2 pt-6">
                <Switch
                  id="is_latest"
                  checked={version.is_latest}
                  onCheckedChange={(checked) => handleInputChange('is_latest', checked)}
                />
                <Label htmlFor="is_latest">Marcar como versão mais recente</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição da Versão</Label>
              <Textarea
                id="description"
                placeholder="Breve descrição desta versão"
                value={version.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="changelog">Notas da Versão (Changelog)</Label>
              <Textarea
                id="changelog"
                placeholder="Descreva as mudanças, melhorias e correções desta versão"
                value={version.changelog}
                onChange={(e) => handleInputChange('changelog', e.target.value)}
                className="min-h-[150px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="requirements">Requisitos do Sistema (opcional)</Label>
              <Textarea
                id="requirements"
                placeholder="Especifique os requisitos mínimos para esta versão"
                value={version.requirements}
                onChange={(e) => handleInputChange('requirements', e.target.value)}
                className="min-h-[100px]"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(createPageUrl('Systems'))}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? 'Salvando...' : 'Salvar Versão'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}